import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, BarChart3Icon, CompassIcon, RotateCcwIcon } from 'lucide-react';
import { pulse, traffic } from '../../data/pulse';
import { qrPanel, strategy } from '../../data/strategy';
import { SERIES } from './colors';
interface PulseFrameProps {
  children: React.ReactNode;
  /** 0 before the first question, 1–5 during, 6 on the closing snapshot. */
  question: number;
  totalQuestions: number;
  footnote?: string;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /** Clears this delegate's answers and returns to the opening screen. */
  onRestart?: () => void;
  /** Opens the full live results across every question. */
  onOpenData?: () => void;
  /** Opens the internal strategic brief. */
  onOpenStrategy?: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function PulseFrame({
  children,
  question,
  totalQuestions,
  footnote,
  onBack,
  onNext,
  nextLabel = 'Continue',
  nextDisabled = false,
  onRestart,
  onOpenData,
  onOpenStrategy
}: PulseFrameProps) {
  const [confirmRestart, setConfirmRestart] = useState(false);
  const inQuestions = question > 0 && question <= totalQuestions;
  return <div className="flex min-h-full w-full items-center justify-center bg-mist px-5 py-8" data-id="element-139" data-mp="1v40plpzluubn">
      <div className="flex w-full max-w-5xl items-center justify-center gap-16" data-id="element-140" data-mp="2itld8m6pefr9">
        <aside className="hidden max-w-xs flex-1 lg:block" data-id="element-141" data-mp="2ivobfti0ff72">
          <p className="text-[10px] uppercase tracking-micro text-mut" data-id="element-142" data-mp="2nrmvx607wd27">{pulse.eyebrow}</p>
          <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] text-navy" data-id="element-143" data-mp="294jx8w05ryda">
            {pulse.railTitle[0]}
            <br data-id="element-144" data-mp="2e1aehmnp900a" />
            {pulse.railTitle[1]}
          </h1>
          <p className="mt-5 text-sm leading-relaxed text-mut" data-id="element-145" data-mp="31s7ec12806v6">{pulse.railBlurb}</p>
          {inQuestions && <p className="mt-6 text-[11px] uppercase tracking-micro text-mut" data-id="element-146" data-mp="36k7iy6t8jny3">
              Question {question} of {totalQuestions}
            </p>}
          <div className="mt-5 flex flex-wrap gap-2" data-id="element-147" data-mp="3bg1tuxmrqpfv">
            {onOpenData && <button type="button" onClick={onOpenData} className="flex items-center gap-2 rounded-full border border-line px-3.5 py-1.5 text-[11px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-148" data-mp="3be8ormrt0d9z">
                <BarChart3Icon className="h-3.5 w-3.5 text-blue" aria-hidden="true" data-id="element-149" data-mp="2soirmt0kjfew" />
                {pulse.dataLabel}
              </button>}
            {onOpenStrategy && <button type="button" onClick={onOpenStrategy} className="flex items-center gap-2 rounded-full border border-line px-3.5 py-1.5 text-[11px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-150" data-mp="2jpor4rk4a4w2">
                <CompassIcon className="h-3.5 w-3.5 text-blue" aria-hidden="true" data-id="element-151" data-mp="pb1dhyv95wyk" />
                {strategy.label}
              </button>}
          </div>

          <figure className="mt-6 rounded-2xl border border-line bg-paper px-4 py-4" data-id="element-152" data-mp="z7ctozxyr6ya">
            <figcaption className="text-[9px] uppercase tracking-micro text-mut" data-id="element-153" data-mp="13754zkeqrr7c">
              {qrPanel.eyebrow}
            </figcaption>
            <img src={qrPanel.src} alt={qrPanel.alt} className="mx-auto mt-2 w-40 select-none" draggable={false} data-id="element-154" data-mp="6gcmyh88zp2x" />
            <p className="mt-1 text-center text-[11.5px] font-medium leading-snug text-navy" data-id="element-155" data-mp="fhkmf2mi2vyl">
              {qrPanel.caption}
            </p>
            <ul className="mt-3 flex flex-col gap-1.5 border-t border-line pt-3" data-id="element-156" data-mp="3jxbverof0jsg">
              {traffic.source.map((slice, i) => {
              const total = traffic.source.reduce((sum, s) => sum + s.count, 0);
              const pct = total ? Math.round(slice.count / total * 100) : 0;
              return <li key={slice.id} className="flex items-center gap-2" data-id="element-157" data-mp="2zw0mvw1cfwia" data-mpi={i}>
                    <span className={`h-2 w-2 shrink-0 rounded-sm ${SERIES[i % SERIES.length]}`} aria-hidden="true" data-id="element-158" data-mp="3ejlr5t9yg1rs" />
                    <span className="min-w-0 flex-1 truncate text-[11px] text-mut" data-id="element-159" data-mp="3nkju088nq38s">
                      {slice.label}
                    </span>
                    <span className="shrink-0 text-[11px] font-semibold tabular-nums text-navy" data-id="element-160" data-mp="3ikffgnrb7il7">
                      {pct}%
                    </span>
                  </li>;
            })}
            </ul>
            <p className="mt-3 text-[10px] leading-snug text-mut" data-id="element-161" data-mp="3skek49klyoss">{qrPanel.note}</p>
          </figure>

          <p className="mt-6 border-t border-line pt-4 text-[11px] leading-relaxed text-mut" data-id="element-162" data-mp="234yolwzlx1ld">
            {pulse.railDisclaimer}
          </p>
        </aside>

        <div className="flex w-full max-w-[400px] flex-col" data-id="element-163" data-mp="dnb8urxfuq3w">
          <div className="flex aspect-[9/16] w-full flex-col overflow-hidden rounded-[34px] border border-line bg-paper shadow-soft" data-id="element-164" data-mp="imo6m7pulhl9">
            <div className="flex items-center justify-between gap-3 px-6 pt-5" data-id="element-165" data-mp="3yjksuvi0py9">
              <span className="truncate text-[9px] uppercase tracking-micro text-mut" data-id="element-166" data-mp="8wl0r6xmo95i">
                {pulse.eyebrow}
              </span>
              <span className="flex shrink-0 items-center gap-2" data-id="element-167" data-mp="3r1o9d1msz3ao">
                {inQuestions && <span className="text-[9px] uppercase tracking-micro text-mut" data-id="element-168" data-mp="3v5md5pn2p2a2">
                    {question} / {totalQuestions}
                  </span>}
                {onOpenData && <button type="button" onClick={onOpenData} aria-label="See the live results across all questions" className="flex h-7 w-7 items-center justify-center rounded-full border border-line text-mut transition-colors duration-150 hover:border-blue hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-169" data-mp="3m3mvqw85bw6l">
                    <BarChart3Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-170" data-mp="18wescuyvpz9i" />
                  </button>}
                {onRestart && <button type="button" onClick={() => setConfirmRestart(open => !open)} aria-label="Start over" aria-expanded={confirmRestart} className={`flex h-7 w-7 items-center justify-center rounded-full border transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue ${confirmRestart ? 'border-blue bg-blue/[0.08] text-blue' : 'border-line text-mut hover:border-blue hover:text-navy'}`} data-id="element-171" data-mp="13zeo8pllb6l1">
                    <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-172" data-mp="2nu9bl0tz7f1u" />
                  </button>}
              </span>
            </div>

            <AnimatePresence initial={false} data-id="element-173" data-mp="2rxffi1tm8u55">
              {confirmRestart && onRestart && <motion.div initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.16,
              ease: EASE
            }} className="mx-6 mt-3 rounded-xl border border-line bg-mist px-3.5 py-3" data-id="element-174" data-mp="2wwilbohf74e6">
                  <p className="text-[11.5px] leading-snug text-navy" data-id="element-175" data-mp="3fkf8asyu2zwi">
                    Start over and clear your answers?
                  </p>
                  <div className="mt-2.5 flex gap-2" data-id="element-176" data-mp="3kj8jufuk8gu1">
                    <button type="button" onClick={() => {
                  setConfirmRestart(false);
                  onRestart();
                }} className="h-8 flex-1 rounded-full bg-navy text-[11.5px] font-semibold text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-177" data-mp="3pjw9oy71yrmo">
                      Start over
                    </button>
                    <button type="button" onClick={() => setConfirmRestart(false)} className="h-8 flex-1 rounded-full border border-line bg-paper text-[11.5px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-178" data-mp="nq55imww51mu">
                      Keep going
                    </button>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {inQuestions && <div className="mt-3 flex gap-1 px-6" aria-hidden="true" data-id="element-179" data-mp="38et9avgkw0xt">
                {Array.from({
              length: totalQuestions
            }, (_, i) => <span key={i} className={`h-0.5 flex-1 rounded-full ${i < question ? 'bg-blue' : 'bg-line'}`} data-id="element-180" data-mp="1nvpepol6ix7n" />)}
              </div>}

            <div className="flex flex-1 flex-col overflow-y-auto px-6 pb-5 pt-4" data-id="element-181" data-mp="1jwgmio5sf7e7">{children}</div>

            {(onNext || onBack || footnote) && <div className="border-t border-line px-6 py-4" data-id="element-182" data-mp="2bmuw486rpxk2">
                {footnote && <p className="mb-3 text-[9px] leading-relaxed text-mut" data-id="element-183" data-mp="2bks7nsl9kchg">{footnote}</p>}
                {(onNext || onBack) && <div className="flex items-center gap-2" data-id="element-184" data-mp="22lx12feb50dm">
                    {onBack && <button type="button" onClick={onBack} aria-label="Previous screen" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line text-mut transition-colors duration-150 hover:border-mut hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-185" data-mp="m8ge06q998xj">
                        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-186" data-mp="1z5m7cgj0cvpj" />
                      </button>}
                    {onNext && <button type="button" onClick={onNext} disabled={nextDisabled} className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-navy px-5 text-[13px] font-semibold text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 disabled:opacity-30" data-id="element-187" data-mp="1q57khcqeb8jx">
                        {nextLabel}
                        <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-188" data-mp="3fjvk0ddw0w0j" />
                      </button>}
                  </div>}
              </div>}
          </div>
        </div>
      </div>
    </div>;
}